// src/pages/GameClient.jsx
// Мир игры — операторский режим.
// Итерация 4: Three.js сцена + WorldTab.
// Итерация 5: VitalsBlock (полоски здоровья/энергии + индикаторы голода/жажды).
// Итерация 6: Navigator (structure / surface / system) в левой панели.
// Итерация 7: InventoryPanel (инвентарь через useInventory, WS + HTTP fallback).
// Итерация 8.3c: useStructureInfo (структура целиком) + selectedDeckId (фильтр модулей).
// Итерация 9.6: режимы просмотра (object / character) + summary_mesh + skybox.
// ⚠️ useGameSession() — только на верхнем уровне (вне <Canvas>).
//    WorldScene получает данные ТОЛЬКО через props.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Typography, Button, Alert, CircularProgress,
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent,
  IconButton, Chip, Tab, Tabs, Divider, Select, MenuItem,
  FormControl, InputLabel, LinearProgress, Tooltip, Badge, Fade,
  Stack, Avatar, alpha, TextField, List, ListItem, ListItemIcon, ListItemText,
  Popper,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Explore as ExploreIcon,
  Public as PublicIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  Psychology as PsychologyIcon,
  Route as RouteIcon,
  Send as SendIcon,
  Help as HelpIcon,
  Terminal as TerminalIcon,
  Bolt as BoltIcon,
  Construction as ConstructionIcon,
  GpsFixed as GpsFixedIcon,
  Stars as StarsIcon,
  Build as BuildIcon,
} from '@mui/icons-material';

import { useGameSession } from '../components/game_client/GameSessionContext';
import CharacterCard from '../components/character_card/CharacterCard';
import WorldScene from '../components/world/WorldScene';
import VitalsBlock from '../components/game_hud/VitalsBlock';
import Navigator from '../components/world/Navigator';
// 🔥 Итерация 7: инвентарь
import InventoryPanel from '../components/game_hud/InventoryPanel';
import Gateway from '../api/gateway';
import { clearChatMessages } from '../store/slices/gameSlice';
// 🔥 Итерация 8.3c: структура + тип локации
import useStructureInfo from '../hooks/useStructureInfo';
import { isStructureType } from '../utils/structureTypes';
// 🔥 Итерация 9.6: режимы просмотра + summary_mesh + skybox
import { useSummaryMesh } from '../hooks/useSummaryMesh';
import { useSkyboxMesh } from '../hooks/useSkyboxMesh';

// ============================================================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================================================

const StatusDot = ({ status, size = 8 }) => {
  const colors = {
    connected: '#22c55e',
    connecting: '#f59e0b',
    error: '#ef4444',
    disconnected: '#6b7280',
    initializing: '#3b82f6',
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: colors[status] || colors.disconnected,
        display: 'inline-block',
        animation: status === 'connecting' || status === 'initializing'
          ? 'pulse 1.5s ease-in-out infinite'
          : 'none',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.5, transform: 'scale(0.85)' },
        },
      }}
    />
  );
};

const ActionChip = ({ action, onAction, disabled }) => {
  const getIcon = () => {
    const iconMap = {
      move: <GpsFixedIcon />,
      explore: <ExploreIcon />,
      rest: <BuildIcon />,
      attack: <BoltIcon />,
      talk: <ChatIcon />,
      use: <ConstructionIcon />,
      pickup: <InventoryIcon />,
      craft: <SettingsIcon />,
    };
    return iconMap[action.type] || iconMap[action.id] || <StarsIcon />;
  };

  const getColor = () => {
    const colorMap = {
      move: 'info',
      explore: 'primary',
      rest: 'warning',
      attack: 'error',
      talk: 'success',
      use: 'secondary',
      pickup: 'info',
      craft: 'secondary',
    };
    return colorMap[action.type] || colorMap[action.id] || 'default';
  };

  return (
    <Tooltip title={action.description || action.label} placement="top" arrow>
      <Chip
        icon={getIcon()}
        label={action.label}
        onClick={() => onAction(action)}
        disabled={disabled}
        color={getColor()}
        variant="outlined"
        size="small"
        sx={{
          height: 28,
          '& .MuiChip-label': { fontWeight: 500, fontSize: '0.7rem', px: 0.75 },
          '& .MuiChip-icon': { fontSize: 16 },
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderColor: (theme) => theme.palette.primary.main,
          },
          transition: 'all 0.2s ease',
        }}
      />
    </Tooltip>
  );
};

// ============================================================================
// КОМАНДНАЯ СТРОКА
// ============================================================================

const CommandBar = ({ onCommand, disabled }) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);

  const COMMANDS = [
    { cmd: '/move', description: 'Переместиться', args: ['x', 'y', 'z'] },
    { cmd: '/look', description: 'Осмотреться', args: [] },
    { cmd: '/inventory', description: 'Показать инвентарь', args: [] },
    { cmd: '/stats', description: 'Показать статистику', args: [] },
    { cmd: '/who', description: 'Кто рядом', args: [] },
    { cmd: '/emote', description: 'Эмоция', args: ['emotion'] },
    { cmd: '/help', description: 'Помощь', args: [] },
  ];

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (value.startsWith('/')) {
      const search = value.toLowerCase();
      const matches = COMMANDS.filter(
        (cmd) =>
          cmd.cmd.toLowerCase().startsWith(search) ||
          cmd.description.toLowerCase().includes(search.slice(1))
      );
      setSuggestions(matches.slice(0, 5));
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || disabled || isProcessing) return;

    setIsProcessing(true);
    const command = input.trim();

    setHistory((prev) => [...prev, command].slice(-50));
    setHistoryIndex(-1);

    try {
      await onCommand(command);
      setInput('');
      setShowSuggestions(false);
    } catch (error) {
      console.error('Command error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length > 0) {
        const newIndex = Math.min(historyIndex + 1, history.length - 1);
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (cmd) => {
    setInput(cmd.cmd + ' ');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <Box ref={anchorRef} sx={{ position: 'relative' }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 0.25,
          bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 1.5,
          transition: 'all 0.3s ease',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: (theme) =>
              `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      >
        <TerminalIcon sx={{ ml: 1, mr: 0.5, color: 'text.secondary', fontSize: 16 }} />

        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          placeholder="Введите команду... (/help)"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isProcessing}
          InputProps={{
            disableUnderline: true,
            sx: { py: 0.25, px: 0.5, fontSize: '0.8rem', '& input': { py: 0.5 } },
          }}
          size="small"
        />

        {isProcessing ? (
          <CircularProgress size={16} sx={{ mx: 0.5 }} />
        ) : (
          <IconButton
            size="small"
            onClick={handleSend}
            disabled={!input.trim() || disabled}
            sx={{
              mx: 0.25,
              color: 'primary.main',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      <Popper
        open={showSuggestions}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth || '100%', maxWidth: 500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={4}
              sx={{
                mt: 0.5,
                maxHeight: 150,
                overflow: 'auto',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <List dense disablePadding>
                {suggestions.map((cmd, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSelectSuggestion(cmd)}
                    sx={{
                      '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                      py: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <HelpIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.7rem' }}
                          >
                            {cmd.cmd}
                          </Typography>
                          <Chip
                            label={cmd.description}
                            size="small"
                            variant="outlined"
                            sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                          />
                        </Box>
                      }
                      secondary={cmd.args.length > 0 ? `Аргументы: ${cmd.args.join(', ')}` : ''}
                      secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.55rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

// ============================================================================
// ВИЗУАЛИЗАТОР РАЗУМА (КОМПАКТНЫЙ)
// ============================================================================

const MindStateVisualizer = ({ emotions, characterState, goals }) => {
  const dominantEmotion = Object.entries(emotions || {}).sort(
    (a, b) => (b[1] || 0) - (a[1] || 0)
  )[0];
  const accentColor = dominantEmotion
    ? dominantEmotion[1] > 0.7
      ? '#ff3d00'
      : '#00e5ff'
    : '#888';
  const currentGoal = goals?.[0];

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
        <PsychologyIcon sx={{ color: accentColor, fontSize: 14 }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.6rem' }}
        >
          Разум
        </Typography>
        <Chip
          label={characterState?.awake ? 'Активен' : 'Сон'}
          size="small"
          sx={{
            height: 14,
            '& .MuiChip-label': { fontSize: '0.45rem', px: 0.5 },
            bgcolor: characterState?.awake ? 'rgba(0,229,255,0.1)' : 'rgba(255,255,255,0.05)',
            color: characterState?.awake ? '#00e5ff' : '#888',
            border: '1px solid',
            borderColor: characterState?.awake ? '#00e5ff' : '#555',
          }}
        />
      </Box>

      <Stack direction="row" spacing={1.5} justifyContent="space-around">
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.5rem', color: 'text.secondary' }}
          >
            Страх
          </Typography>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <CircularProgress
              variant="determinate"
              value={Math.round((emotions?.fear || emotions?.страх || 0) * 100)}
              size={24}
              thickness={3}
              sx={{ color: '#ff3d00' }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.45rem',
                fontWeight: 600,
              }}
            >
              {Math.round((emotions?.fear || emotions?.страх || 0) * 100)}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontSize: '0.5rem', color: 'text.secondary' }}
          >
            Любопыт.
          </Typography>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <CircularProgress
              variant="determinate"
              value={Math.round((emotions?.curiosity || emotions?.любопытство || 0) * 100)}
              size={24}
              thickness={3}
              sx={{ color: '#00e676' }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.45rem',
                fontWeight: 600,
              }}
            >
              {Math.round((emotions?.curiosity || emotions?.любопытство || 0) * 100)}%
            </Typography>
          </Box>
        </Box>
        {currentGoal && (
          <Box sx={{ textAlign: 'center', maxWidth: 60 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.45rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Цель
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.45rem', fontWeight: 500, display: 'block' }}>
              {currentGoal.name?.substring(0, 8) || '—'}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

// ============================================================================
// ИНФОРМАЦИЯ О ЛОКАЦИИ
// ============================================================================

const LocationInfoCard = ({ locationInfo, isLoading }) => {
  if (isLoading) {
    return (
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ py: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Загрузка...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!locationInfo || locationInfo.id === 'unknown') {
    return (
      <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          Информация о локации недоступна
        </Typography>
      </Alert>
    );
  }

  const env = locationInfo.environment || {};

  return (
    <Card sx={{ mb: 1, borderLeft: 3, borderColor: 'primary.main' }}>
      <CardContent sx={{ py: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <LocationIcon color="primary" sx={{ fontSize: 14 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            {locationInfo.name || 'Локация'}
          </Typography>
          {locationInfo.type && locationInfo.type !== 'unknown' && (
            <Chip
              label={locationInfo.type}
              size="small"
              variant="outlined"
              sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem' } }}
            />
          )}
        </Box>
        {locationInfo.description && locationInfo.description !== 'Информация о локации недоступна' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
            {locationInfo.description}
          </Typography>
        )}
        {Object.keys(env).length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mt: 0.25 }}>
            {env.temperature !== undefined && (
              <Chip
                label={`🌡️ ${env.temperature}°C`}
                size="small"
                variant="outlined"
                sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem' } }}
              />
            )}
            {env.gravity !== undefined && (
              <Chip
                label={`🔄 ${env.gravity}g`}
                size="small"
                variant="outlined"
                sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem' } }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// ВКЛАДКА «МИР» (МЕМОИЗИРОВАННЫЙ)
// ============================================================================

const WorldTabInner = ({
  isConnected,
  selectedCharacterName,
  locationInfo,
  loadingLocation,
  availableActions,
  actionInProgress,
  isInitializing,
  onAction,
  onOpenSelector,
}) => {
  if (!isConnected) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <RouteIcon sx={{ fontSize: 36, color: 'text.secondary', opacity: 0.2, mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          Вы не в игре
        </Typography>
        <Button
          variant="contained"
          onClick={onOpenSelector}
          startIcon={<PersonIcon />}
          size="small"
          sx={{ mt: 1, fontSize: '0.7rem' }}
        >
          Выбрать персонажа
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
        🌍 Добро пожаловать, {selectedCharacterName}!
      </Typography>
      <LocationInfoCard locationInfo={locationInfo} isLoading={loadingLocation} />
      {availableActions.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
            <BuildIcon sx={{ fontSize: 14, mr: 0.5 }} /> Действия
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {availableActions.map((action, i) => (
              <ActionChip
                key={i}
                action={action}
                onAction={onAction}
                disabled={actionInProgress || isInitializing}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const WorldTab = React.memo(WorldTabInner, (prev, next) => {
  if (prev.isConnected !== next.isConnected) return false;
  if (prev.selectedCharacterName !== next.selectedCharacterName) return false;
  if (prev.loadingLocation !== next.loadingLocation) return false;
  if (prev.actionInProgress !== next.actionInProgress) return false;
  if (prev.isInitializing !== next.isInitializing) return false;
  if (prev.onAction !== next.onAction) return false;
  if (prev.onOpenSelector !== next.onOpenSelector) return false;

  const a = prev.locationInfo;
  const b = next.locationInfo;
  if (!a || !b) {
    if (a !== b) return false;
  } else {
    if (a.id !== b.id) return false;
    if (a.name !== b.name) return false;
    if (a.type !== b.type) return false;
    if (a.description !== b.description) return false;
    const aEnv = a.environment || {};
    const bEnv = b.environment || {};
    if (aEnv.temperature !== bEnv.temperature) return false;
    if (aEnv.gravity !== bEnv.gravity) return false;
  }

  const pa = prev.availableActions || [];
  const pb = next.availableActions || [];
  if (pa.length !== pb.length) return false;
  for (let i = 0; i < pa.length; i++) {
    if (pa[i]?.id !== pb[i]?.id) return false;
    if (pa[i]?.type !== pb[i]?.type) return false;
    if (pa[i]?.label !== pb[i]?.label) return false;
  }

  return true;
});

// ============================================================================
// ОСНОВНОЙ КОМПОНЕНТ GameClient (Мир)
// ============================================================================

export default function GameClient() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const {
    isConnected,
    isConnecting,
    isInitializing,
    actionInProgress,
    characterState,
    emotions,
    goals,
    selectedCharacter,
    selectedCharacterId,
    locationInfo,
    availableActions,
    connect,
    disconnect,
    sendAction,
    selectCharacter,
    loadLocationInfo,
    loadAvailableActions,
    updatePosition,
    refreshState,
    addGameEvent,
    clearEvents,
    loadEmotions,
    loadGoals,
    loadTasks,
    loadIntents,
    loadConsciousness,
    // 🔥 Итерация 5: витальность
    vitals,
    characterMeta,
    vitalsIsLive,
    vitalsIsStale,
    refetchVitals,
    // 🔥 Итерация 7: инвентарь
    inventory,
    inventoryLoading,
    inventoryError,
    refetchInventory,
  } = useGameSession();

  // 🔥 Итерация 8.3c: структура (модули) для 3D-визуализации
  const shouldLoadStructure =
    isStructureType(locationInfo?.type) && !!locationInfo?.id;

  const { structureInfo } = useStructureInfo(
    shouldLoadStructure ? locationInfo.id : null,
    shouldLoadStructure ? locationInfo.type : null
  );

  // 🔥 Итерация 9.6: summary_mesh (для mode="object")
  const { mesh: summaryMesh } = useSummaryMesh(
    shouldLoadStructure ? locationInfo?.type : null,
    shouldLoadStructure ? locationInfo?.id : null,
    { enabled: shouldLoadStructure }
  );

  // 🔥 Итерация 9.6: skybox (звёзды, планеты, луны) по system_id
  const systemId =
    characterMeta?.system_id ||
    locationInfo?.system_id ||
    characterState?.system_id ||
    null;

  const { skybox } = useSkyboxMesh(systemId, { enabled: !!systemId });

  // 🔥 Итерация 8.3c: выбранная палуба (для фильтра модулей)
  const [selectedDeckId, setSelectedDeckId] = useState(null);

  // 🔥 Итерация 9.6: режим просмотра (object / character)
  const [viewMode, setViewMode] = useState('character');

  const [showSelector, setShowSelector] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [localCharacters, setLocalCharacters] = useState([]);
  const [loadingChars, setLoadingChars] = useState(false);
  const [worlds, setWorlds] = useState([]);
  const [selectedWorldId, setSelectedWorldId] = useState('');
  const [loadingWorlds, setLoadingWorlds] = useState(false);
  const [worldName, setWorldName] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);

  // ===== Загрузка данных =====
  const loadWorlds = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingWorlds(true);
      const worldsData = await Gateway.api.worldManager.getAvailableWorlds();
      const sorted = worldsData.sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return a.name.localeCompare(b.name);
      });
      setWorlds(sorted);
      const active = sorted.find((w) => w.status === 'active') || sorted[0];
      if (active) {
        setSelectedWorldId(active.id);
        setWorldName(active.name);
      }
    } catch (err) {
      console.error('Failed to load worlds:', err);
    } finally {
      setLoadingWorlds(false);
    }
  }, [isAuthenticated]);

  const loadCharacters = useCallback(async (worldId) => {
    if (!user?.id || !worldId) return;
    try {
      setLoadingChars(true);
      const response = await Gateway.api.character.getCharactersByPlayer(user.id);
      const loadedChars = Gateway.utils.characters.extractDataFromResponse(response, 'characters');

      const normalized = loadedChars
        .map((char) => {
          if (typeof char === 'string' || typeof char === 'number') return null;
          const id = Gateway.utils.extractId(char);
          const norm = Gateway.utils.normalizeCharacterPlacement(char);
          return {
            ...norm,
            id,
            in_world: Gateway.utils.isCharacterPlacedInWorld(norm),
            is_active: char.status === 'active' || char.is_active === true,
            current_world_id: char.current_world_id,
            world_id: char.world_id,
          };
        })
        .filter(Boolean);

      const inWorld = normalized.filter((char) => {
        const placed = Gateway.utils.isCharacterPlacedInWorld(char);
        const wid = char.current_world_id || char.world_id;
        return placed && String(wid) === String(worldId);
      });

      setLocalCharacters(inWorld);
    } catch (err) {
      console.error('Failed to load characters:', err);
      setLocalCharacters([]);
    } finally {
      setLoadingChars(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAuthenticated && user?.id) loadWorlds();
  }, [isAuthenticated, user?.id, loadWorlds]);

  useEffect(() => {
    if (selectedWorldId) loadCharacters(selectedWorldId);
  }, [selectedWorldId, loadCharacters]);

  // 🔥 Итерация 8.3c: сброс выбранной палубы при смене структуры
  useEffect(() => {
    setSelectedDeckId(null);
  }, [locationInfo?.id]);

  // Загрузка данных при подключении
  useEffect(() => {
    if (!isConnected || !selectedCharacterId) {
      setLoadingLocation(false);
      return;
    }

    let cancelled = false;
    setLoadingLocation(true);

    const loadData = async () => {
      try {
        await Promise.all([
          loadLocationInfo(selectedCharacterId),
          loadAvailableActions(selectedCharacterId),
          loadEmotions(selectedCharacterId),
          loadGoals(selectedCharacterId),
          loadTasks(selectedCharacterId),
          loadIntents(selectedCharacterId),
          loadConsciousness(selectedCharacterId),
        ]);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingLocation(false);
      }
    };
    loadData();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, selectedCharacterId]);

  // ===== Обработчики =====

  const handleCommand = useCallback(async (command) => {
    const parts = command.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case '/help':
        addGameEvent('system', {
          message: '📋 /move x y z, /look, /inventory, /stats, /who, /emote',
          type: 'info',
        });
        break;
      // 🔥 Итерация 7: /inventory — показать инвентарь
      case '/inventory':
        if (refetchInventory) refetchInventory();
        setActiveTab(1);
        addGameEvent('system', {
          message: `🎒 Инвентарь: ${inventory?.items?.length || 0} предметов`,
          type: 'info',
        });
        break;
      case '/stats':
        await refreshState();
        addGameEvent('system', {
          message: `📊 Уровень: ${characterState?.level || 1}, HP: ${Math.round(vitals?.health?.current ?? characterState?.health?.current ?? 0)}%, Энергия: ${Math.round(vitals?.energy?.current ?? characterState?.energy?.current ?? 0)}%`,
          type: 'info',
        });
        break;
      case '/move':
        if (args.length >= 3) {
          const [x, y, z] = args.map(Number);
          if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
            try {
              await updatePosition({ x, y, z });
              addGameEvent('system', {
                message: `🚶 Перемещение на ${x}, ${y}, ${z}`,
                type: 'success',
              });
            } catch (err) {
              addGameEvent('system', {
                message: `❌ Ошибка: ${err.message}`,
                type: 'error',
              });
            }
          }
        }
        break;
      case '/look':
        addGameEvent('system', {
          message: `👀 Вы в ${locationInfo?.name || 'неизвестной локации'}`,
          type: 'info',
        });
        break;
      default:
        addGameEvent('system', {
          message: `❌ Неизвестная команда: ${cmd}. Используйте /help`,
          type: 'warning',
        });
    }
  }, [addGameEvent, refreshState, characterState, updatePosition, locationInfo, vitals, inventory, refetchInventory]);

  const handleSelectCharacter = async (character) => {
    if (!character?.id) return false;
    if (!Gateway.utils.isCharacterPlacedInWorld(character)) {
      setError('Персонаж не размещён в мире');
      return false;
    }
    try {
      setError(null);
      const result = await selectCharacter(character, true);
      if (result) {
        setSuccess(`Персонаж "${character.name}" выбран`);
        setTimeout(() => setSuccess(null), 2000);
        setShowSelector(false);
        return true;
      }
      return false;
    } catch (err) {
      setError(`Ошибка: ${err.message}`);
      return false;
    }
  };

  const handleConnect = async () => {
    if (!selectedCharacter) {
      setError('Сначала выберите персонажа');
      return;
    }
    try {
      setError(null);
      setSuccess('Подключение...');
      await connect(selectedCharacter.id);
      setSuccess('Подключено!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(`Ошибка: ${err.message}`);
    }
  };

  const handleAction = useCallback(async (action) => {
    if (!isConnected || actionInProgress) return;
    try {
      await sendAction(action.type || action.id, action.params || {});
      addGameEvent('success', { message: `✅ ${action.label}`, type: 'success' });
      await refreshState();
      if (selectedCharacterId) {
        await Promise.all([
          loadLocationInfo(selectedCharacterId),
          loadAvailableActions(selectedCharacterId),
          loadEmotions(selectedCharacterId),
          loadGoals(selectedCharacterId),
          loadTasks(selectedCharacterId),
          loadIntents(selectedCharacterId),
          loadConsciousness(selectedCharacterId),
        ]);
      }
    } catch (err) {
      addGameEvent('error', { message: `❌ Ошибка: ${err.message}`, type: 'error' });
      setError(`Ошибка: ${err.message}`);
    }
  }, [isConnected, actionInProgress, sendAction, addGameEvent, refreshState, selectedCharacterId, loadLocationInfo, loadAvailableActions, loadEmotions, loadGoals, loadTasks, loadIntents, loadConsciousness]);

  const handleDisconnect = () => {
    disconnect();
    addGameEvent('system', { message: 'Отключено', type: 'info' });
    setSuccess('Отключено');
    setTimeout(() => setSuccess(null), 1500);
  };

  const handleRefresh = () => {
    loadWorlds();
    if (selectedWorldId) loadCharacters(selectedWorldId);
    refreshState();
    if (refetchVitals) refetchVitals();
    if (refetchInventory) refetchInventory();
    if (selectedCharacterId && isConnected) {
      Promise.all([
        loadLocationInfo(selectedCharacterId),
        loadAvailableActions(selectedCharacterId),
        loadEmotions(selectedCharacterId),
        loadGoals(selectedCharacterId),
        loadTasks(selectedCharacterId),
        loadIntents(selectedCharacterId),
        loadConsciousness(selectedCharacterId),
      ]);
    }
  };

  const handleCreateCharacter = () => {
    if (!isAuthenticated) return;
    navigate('/designer', { state: { returnTo: '/game/world' } });
  };

  const handleClearEvents = () => {
    clearEvents();
    dispatch(clearChatMessages());
  };

  const handleWorldChange = (event) => {
    setSelectedWorldId(event.target.value);
  };

  const handlePlayCharacter = async (characterId) => {
    const character = localCharacters.find((c) => c && c.id === characterId);
    if (character) await handleSelectCharacter(character);
    else setError('Персонаж не найден');
  };

  const handleEditCharacter = (characterId) => {
    navigate(`/designer?character=${characterId}`, { state: { returnTo: '/game/world' } });
  };

  const handleOpenSelector = useCallback(() => setShowSelector(true), []);

  // ===== Рендер =====

  const connectionStatus = isConnected
    ? 'connected'
    : isConnecting
      ? 'connecting'
      : isInitializing
        ? 'initializing'
        : 'disconnected';

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <PublicIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5, opacity: 0.3 }} />
          <Typography variant="h6" gutterBottom>Доступ ограничен</Typography>
          <Typography color="text.secondary">Пожалуйста, войдите в систему</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Container maxWidth="xl" sx={{ py: 1, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>

        {/* ВЕРХНЯЯ ПАНЕЛЬ */}
        <Box sx={{ mb: 0.5, flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>🚀 Мир</Typography>
              <Chip
                size="small"
                label={locationInfo?.name || worldName || 'Выберите мир'}
                color="primary"
                variant="outlined"
                icon={<PublicIcon />}
                sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.65rem' } }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StatusDot status={connectionStatus} size={7} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.6rem' }}>
                  {connectionStatus === 'connected' ? 'В игре' : connectionStatus === 'connecting' ? 'Подключение...' : connectionStatus === 'initializing' ? 'Инициализация...' : 'Офлайн'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<PsychologyIcon />}
                onClick={() => navigate('/game/cabinet')}
                sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 28 }}
              >
                В КАБИНЕТ →
              </Button>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loadingChars || loadingWorlds} sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 28 }}>
                Обновить
              </Button>
              {selectedCharacter && isConnected && (
                <Button size="small" variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleDisconnect} sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 28 }}>
                  Выйти
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* УВЕДОМЛЕНИЯ */}
        {error && (
          <Alert severity="error" sx={{ mb: 0.5, py: 0.25 }} onClose={() => setError(null)}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 0.5, py: 0.25 }} onClose={() => setSuccess(null)}>
            <Typography variant="caption">{success}</Typography>
          </Alert>
        )}

        {/* КОМАНДНАЯ СТРОКА */}
        <Box sx={{ mb: 0.5, flexShrink: 0 }}>
          <CommandBar onCommand={handleCommand} disabled={!isConnected && !selectedCharacter} />
        </Box>

        {/* ОСНОВНАЯ СЕТКА */}
        <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>

          {/* ЛЕВАЯ КОЛОНКА */}
          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Paper sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
              {!selectedCharacter ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <ExploreIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Персонаж не выбран</Typography>
                  <Button variant="contained" onClick={() => setShowSelector(true)} startIcon={<PersonIcon />} size="small" sx={{ mt: 1, fontSize: '0.7rem' }}>
                    Выбрать
                  </Button>
                </Box>
              ) : (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
                      {selectedCharacter.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {selectedCharacter.name || 'Без имени'}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={`Ур. ${characterState?.level || selectedCharacter.level || 1}`} size="small" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem' } }} />
                        <Chip label={characterState?.race || selectedCharacter.race || '—'} size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem' } }} />
                      </Stack>
                    </Box>
                    <IconButton size="small" onClick={() => setShowSelector(true)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  {/* 🔥 Итерация 5: VitalsBlock вместо старых полосок */}
                  <Box sx={{ mb: 0.75 }}>
                    <VitalsBlock
                      vitals={vitals}
                      characterMeta={characterMeta}
                      isLive={vitalsIsLive}
                      isStale={vitalsIsStale}
                    />
                  </Box>

                  <Divider sx={{ my: 0.5 }} />

                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <MindStateVisualizer emotions={emotions} characterState={characterState} goals={goals} />
                  </Box>

                  <Divider sx={{ my: 0.5 }} />

                  {/* 🔥 Итерация 6 + 8.3c: навигатор (structure / surface / system) + структура через prop */}
                  <Box sx={{ mb: 0.75 }}>
                    <Navigator
                      characterMeta={characterMeta}
                      locationInfo={locationInfo}
                      structureInfo={structureInfo}
                      onDeckSelect={(deckId) => setSelectedDeckId(deckId)}
                    />
                  </Box>

                  <Divider sx={{ my: 0.5 }} />

                  <Stack spacing={0.5} sx={{ mt: 'auto' }}>
                    {!isConnected && !isConnecting && (
                      <Button fullWidth variant="contained" color="success" onClick={() => handleConnect()} disabled={!selectedCharacter} size="small" sx={{ fontSize: '0.65rem', py: 0.25 }}>
                        Войти в игру
                      </Button>
                    )}
                    {isConnecting && (
                      <Button fullWidth variant="contained" color="warning" disabled startIcon={<CircularProgress size={14} />} size="small" sx={{ fontSize: '0.65rem', py: 0.25 }}>
                        Подключение...
                      </Button>
                    )}
                    {isConnected && (
                      <Button fullWidth variant="outlined" color="primary" onClick={() => setShowSelector(true)} size="small" sx={{ fontSize: '0.65rem', py: 0.25 }}>
                        Сменить персонажа
                      </Button>
                    )}
                    <Button fullWidth variant="text" size="small" onClick={() => navigate('/designer')} startIcon={<AddIcon />} sx={{ fontSize: '0.65rem', py: 0.25 }}>
                      Создать
                    </Button>
                  </Stack>
                </>
              )}
            </Paper>
          </Grid>

          {/* ПРАВАЯ КОЛОНКА */}
          <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Paper sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'background.paper', borderRadius: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1, flexShrink: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  sx={{ minHeight: 32 }}
                  TabIndicatorProps={{ sx: { height: 2, borderRadius: '2px 2px 0 0' } }}
                >
                  <Tab icon={<ExploreIcon sx={{ fontSize: 16 }} />} label="Мир" sx={{ minHeight: 32, textTransform: 'none', fontWeight: 600, fontSize: '0.65rem', py: 0.25 }} />
                  <Tab icon={<InventoryIcon sx={{ fontSize: 16 }} />} label="Инвентарь" sx={{ minHeight: 32, textTransform: 'none', fontWeight: 600, fontSize: '0.65rem', py: 0.25 }} />
                </Tabs>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 1.5, minHeight: 0, position: 'relative' }}>

                {activeTab === 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    {/* === 3D СЦЕНА (Итерация 4 + 8.3b + 9.6) === */}
                    <Box sx={{ flex: 1, minHeight: 320, mb: 1, position: 'relative' }}>
                      <WorldScene
                        locationInfo={locationInfo}
                        characterState={characterState}
                        characterMeta={characterMeta}
                        installedModules={structureInfo?.installed_modules || []}
                        currentDeckId={selectedDeckId || characterMeta?.deck_id || null}
                        mode={viewMode}
                        summaryMesh={summaryMesh}
                        skybox={skybox}
                      />

                      {/* 🔥 Переключатель режимов (object / character) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                        }}
                      >
                        <ToggleButtonGroup
                          value={viewMode}
                          exclusive
                          size="small"
                          onChange={(e, v) => v && setViewMode(v)}
                          sx={{
                            bgcolor: 'rgba(0,0,0,0.55)',
                            backdropFilter: 'blur(6px)',
                            '& .MuiToggleButton-root': {
                              color: 'white',
                              borderColor: 'rgba(255,255,255,0.25)',
                              py: 0.25,
                              px: 1,
                              fontSize: '0.7rem',
                              '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                              },
                            },
                          }}
                        >
                          <ToggleButton value="character" aria-label="Персонаж">
                            👤
                          </ToggleButton>
                          <ToggleButton value="object" aria-label="Объект">
                            🔭
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Box>
                    {/* === ВРЕМЕННЫЙ WorldTab (перенесём в HUD на 4.6) === */}
                    <WorldTab
                      isConnected={isConnected}
                      selectedCharacterName={selectedCharacter?.name}
                      locationInfo={locationInfo}
                      loadingLocation={loadingLocation}
                      availableActions={availableActions || []}
                      actionInProgress={actionInProgress}
                      isInitializing={isInitializing}
                      onAction={handleAction}
                      onOpenSelector={handleOpenSelector}
                    />
                  </Box>
                )}

                {/* 🔥 Итерация 7: инвентарь через InventoryPanel */}
                {activeTab === 1 && (
                  <InventoryPanel
                    inventory={inventory}
                    loading={inventoryLoading}
                    error={inventoryError}
                    onRefetch={refetchInventory}
                  />
                )}

              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* ДИАЛОГ ВЫБОРА ПЕРСОНАЖА */}
        <Dialog open={showSelector} onClose={() => setShowSelector(false)} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '40vh' } }}>
          <DialogTitle sx={{ py: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1rem' }}>Выберите персонажа</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {selectedWorldId ? `Мир: ${worldName}` : 'Выберите мир'}
                </Typography>
              </Box>
              <IconButton onClick={() => setShowSelector(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ py: 1 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Игровой мир</InputLabel>
              <Select value={selectedWorldId} onChange={handleWorldChange} label="Игровой мир" disabled={loadingWorlds}>
                {worlds.map((world) => (
                  <MenuItem key={world.id} value={world.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PublicIcon fontSize="small" />
                      <Typography variant="body2">{world.name}</Typography>
                      <Chip
                        label={world.status === 'active' ? 'Активен' : 'Неактивен'}
                        size="small"
                        color={world.status === 'active' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem' } }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loadingChars ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
                <CircularProgress size={28} />
              </Box>
            ) : localCharacters.length === 0 ? (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <PublicIcon sx={{ fontSize: 36, color: 'text.secondary', opacity: 0.3 }} />
                <Typography variant="body2" color="text.secondary">Нет персонажей в этом мире</Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
                  <Button variant="contained" onClick={handleCreateCharacter} size="small">Создать</Button>
                  <Button variant="outlined" onClick={() => navigate('/designer')} size="small">Конструктор</Button>
                </Box>
              </Paper>
            ) : (
              <Grid container spacing={1} sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {localCharacters.map((character) => {
                  const id = Gateway.utils.extractId(character);
                  const isPlaced = Gateway.utils.isCharacterPlacedInWorld(character);
                  return (
                    <Grid item xs={12} md={6} key={id}>
                      <CharacterCard
                        character={character}
                        isSelected={selectedCharacter?.id === id}
                        onSelect={() => handleSelectCharacter(character)}
                        onPlay={() => handlePlayCharacter(id)}
                        onEdit={() => handleEditCharacter(id)}
                        showActivation={false}
                        showWorldStatus={true}
                        currentWorldId={selectedWorldId}
                        isPlacedInWorld={isPlaced}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button onClick={handleRefresh} startIcon={<RefreshIcon />} disabled={loadingChars || loadingWorlds} size="small">Обновить</Button>
            <Button onClick={() => setShowSelector(false)} variant="contained" size="small">Продолжить</Button>
          </DialogActions>
        </Dialog>

        {/* ИНДИКАТОР ЗАГРУЗКИ */}
        {(actionInProgress || isInitializing) && (
          <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, zIndex: 9999 }} />
        )}

      </Container>
    </Box>
  );
}
